import React, { useState, useEffect } from 'react';
import { getJoinRequests, approveJoinRequest, denyJoinRequest } from '../../services/leagueService';
import { IconCheck, IconX } from '@tabler/icons-react';

const JoinRequestsTab = ({ leagueId, onUpdate }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);

  useEffect(() => {
    loadRequests();
  }, [leagueId]);

  const loadRequests = async () => {
    try {
      setLoading(true);
      const requestsData = await getJoinRequests(leagueId);
      setRequests(requestsData);
    } catch (error) {
      console.error('Error loading join requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (request) => {
    if (!window.confirm(`Approve ${request.firstName} ${request.lastName} to join?`)) {
      return;
    }

    setProcessing(request.userId);
    try {
      await approveJoinRequest(leagueId, request.userId, {
        firstName: request.firstName,
        lastName: request.lastName,
        email: request.email
      });
      loadRequests();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error approving request:', error);
      alert('Failed to approve request. Please try again.');
    } finally {
      setProcessing(null);
    }
  };

  const handleDeny = async (request) => {
    if (!window.confirm(`Deny ${request.firstName} ${request.lastName}'s request?`)) {
      return;
    }

    setProcessing(request.userId);
    try {
      await denyJoinRequest(leagueId, request.userId);
      loadRequests();
    } catch (error) {
      console.error('Error denying request:', error);
      alert('Failed to deny request. Please try again.');
    } finally {
      setProcessing(null);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  return (
    <section className="ls-join-requests-tab">
      <header className="ls-requests-header">
        <h2>Join Requests ({requests.length})</h2>
      </header>

      {loading ? (
        <div className="ls-loading-requests">
          <p>Loading requests...</p>
        </div>
      ) : requests.length === 0 ? (
        <div className="ls-empty-requests">
          <p>No pending join requests.</p>
        </div>
      ) : (
        <div className="ls-requests-list">
          {requests.map(request => (
            <article key={request.userId} className="ls-request-card">
              <div className="ls-request-info">
                <div className="ls-request-avatar">
                  {request.firstName?.[0]}{request.lastName?.[0]}
                </div>
                <div className="ls-request-details">
                  <h4>{request.firstName} {request.lastName}</h4>
                  <p className="ls-request-email">{request.email}</p>
                  <span className="ls-request-date">
                    Requested {formatDate(request.requestedAt)}
                  </span>
                </div>
              </div>

              <div className="ls-request-actions">
                <button
                  onClick={() => handleApprove(request)}
                  className="ls-btn-approve"
                  disabled={processing === request.userId}
                >
                  <IconCheck size={18} />
                  <span>Approve</span>
                </button>
                <button
                  onClick={() => handleDeny(request)}
                  className="ls-btn-deny"
                  disabled={processing === request.userId}
                >
                  <IconX size={18} />
                  <span>Deny</span>
                </button>
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
};

export default JoinRequestsTab;
